import React, { useState, useEffect } from "react";
import {
  Search,
  MapPin,
  Star,
  Phone,
  Clock,
  DollarSign,
  Filter,
  ArrowUpDown,
  Heart,
  MessageCircle,
  Navigation,
  Award,
  Building2,
} from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { useTheme } from "../contexts/ThemeContext";

const VeterinaryPriceComparisonPage = () => {
  const { themeClasses } = useTheme();

  const [searchParams, setSearchParams] = useState({
    treatment: "",
    city: "",
    maxDistance: 50,
    sortBy: "price",
    sortOrder: "asc",
  });

  const [userLocation, setUserLocation] = useState(null);
  const [clinics, setClinics] = useState([]);
  // const [treatments, setTreatments] = useState([]); // Unused for now
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [priceStats, setPriceStats] = useState(null);

  // Common treatments for quick selection
  const commonTreatments = [
    { id: "consultation", name: "Konsultasjon 15 min", icon: "🩺" },
    { id: "vaccine_dog", name: "Vaksine hund", icon: "💉" },
    { id: "vaccine_cat", name: "Vaksine katt", icon: "💉" },
    { id: "castration_male", name: "Kastrering hannkatt", icon: "⚕️" },
    { id: "castration_female", name: "Kastrering hunnkatt", icon: "⚕️" },
    { id: "dental_cleaning", name: "Tannrens", icon: "🦷" },
    { id: "xray_hd", name: "HD-røntgen", icon: "📷" },
    { id: "microchip", name: "ID-merking/chip", icon: "🔬" },
    { id: "ultrasound", name: "Ultralyd drektighet", icon: "📡" },
    { id: "nail_clipping", name: "Kloklipper", icon: "✂️" },
  ];

  // Get user's location for distance calculations
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.log("Location access denied:", error);
        }
      );
    }
  }, []);

  // Search for clinics
  const searchClinics = async () => {
    if (!searchParams.treatment) {
      setError("Please select a treatment to compare prices");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const searchVeterinaryClinics = httpsCallable(
        functions,
        "searchVeterinaryClinics"
      );

      const response = await searchVeterinaryClinics({
        treatment: searchParams.treatment,
        city: searchParams.city || null,
        maxDistance: searchParams.maxDistance,
        userLocation: userLocation,
        sortBy: searchParams.sortBy,
        sortOrder: searchParams.sortOrder,
        limit: 20,
      });

      if (response.data.success) {
        setClinics(response.data.clinics);

        // Get price statistics
        await getTreatmentPriceStats();
      }
    } catch (error) {
      console.error("Error searching clinics:", error);
      setError("Failed to search clinics. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Get treatment price statistics
  const getTreatmentPriceStats = async () => {
    try {
      const getTreatmentPrices = httpsCallable(functions, "getTreatmentPrices");

      const response = await getTreatmentPrices({
        treatment: searchParams.treatment,
        city: searchParams.city || null,
        country: "Norway",
      });

      if (response.data.success) {
        setPriceStats(response.data.priceStatistics);
      }
    } catch (error) {
      console.error("Error getting price stats:", error);
    }
  };

  // Handle treatment selection
  const selectTreatment = (treatmentId) => {
    setSearchParams((prev) => ({ ...prev, treatment: treatmentId }));
  };

  // Handle sorting change
  const changeSorting = (sortBy) => {
    setSearchParams((prev) => ({
      ...prev,
      sortBy,
      sortOrder:
        prev.sortBy === sortBy && prev.sortOrder === "asc" ? "desc" : "asc",
    }));
  };

  // Format price
  const formatPrice = (price, currency = "NOK") => {
    return `${price?.toLocaleString("no-NO")} ${currency}`;
  };

  // Get distance text
  const getDistanceText = (distance) => {
    if (!distance) return "";
    return distance < 1 ? `${Math.round(distance * 1000)}m` : `${distance}km`;
  };

  return (
    <div className={`min-h-screen ${themeClasses.bg.primary}`}>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1
            className={`text-4xl font-bold ${themeClasses.text.primary} mb-4`}
          >
            🐾 Sammenlign Veterinærpriser
          </h1>
          <p className={`text-xl ${themeClasses.text.secondary} mb-6`}>
            Finn den beste prisen for din kjæledyrs behandling
          </p>
        </div>

        {/* Treatment Selection */}
        <div
          className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6 mb-6`}
        >
          <h2
            className={`text-2xl font-semibold ${themeClasses.text.primary} mb-4`}
          >
            Velg behandling
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {commonTreatments.map((treatment) => (
              <button
                key={treatment.id}
                onClick={() => selectTreatment(treatment.id)}
                className={`p-4 rounded-lg border transition-all duration-200 ${
                  searchParams.treatment === treatment.id
                    ? `${themeClasses.bg.button} text-white border-blue-500`
                    : `${themeClasses.bg.secondary} ${themeClasses.text.primary} ${themeClasses.border.primary} hover:border-blue-400`
                }`}
              >
                <div className="text-2xl mb-2">{treatment.icon}</div>
                <div className="text-sm font-medium">{treatment.name}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Search Filters */}
        <div
          className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6 mb-6`}
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* City Filter */}
            <div>
              <label
                className={`block text-sm font-medium ${themeClasses.text.primary} mb-2`}
              >
                <MapPin className="w-4 h-4 inline mr-1" />
                By/Kommune
              </label>
              <input
                type="text"
                value={searchParams.city}
                onChange={(e) =>
                  setSearchParams((prev) => ({ ...prev, city: e.target.value }))
                }
                placeholder="f.eks. Oslo"
                className={`w-full px-4 py-2 rounded-lg ${themeClasses.bg.input} border ${themeClasses.border.primary} ${themeClasses.text.primary}`}
              />
            </div>

            {/* Distance Filter */}
            <div>
              <label
                className={`block text-sm font-medium ${themeClasses.text.primary} mb-2`}
              >
                <Navigation className="w-4 h-4 inline mr-1" />
                Maks avstand
              </label>
              <select
                value={searchParams.maxDistance}
                onChange={(e) =>
                  setSearchParams((prev) => ({
                    ...prev,
                    maxDistance: Number(e.target.value),
                  }))
                }
                className={`w-full px-4 py-2 rounded-lg ${themeClasses.bg.input} border ${themeClasses.border.primary} ${themeClasses.text.primary}`}
              >
                <option value={10}>10 km</option>
                <option value={25}>25 km</option>
                <option value={50}>50 km</option>
                <option value={100}>100 km</option>
                <option value={999}>Hele landet</option>
              </select>
            </div>

            {/* Sort By */}
            <div>
              <label
                className={`block text-sm font-medium ${themeClasses.text.primary} mb-2`}
              >
                <Filter className="w-4 h-4 inline mr-1" />
                Sorter etter
              </label>
              <select
                value={searchParams.sortBy}
                onChange={(e) =>
                  setSearchParams((prev) => ({
                    ...prev,
                    sortBy: e.target.value,
                  }))
                }
                className={`w-full px-4 py-2 rounded-lg ${themeClasses.bg.input} border ${themeClasses.border.primary} ${themeClasses.text.primary}`}
              >
                <option value="price">Pris</option>
                <option value="distance">Avstand</option>
                <option value="rating">Vurdering</option>
              </select>
            </div>

            {/* Search Button */}
            <div className="flex items-end">
              <button
                onClick={searchClinics}
                disabled={loading || !searchParams.treatment}
                className={`w-full ${themeClasses.bg.button} text-white px-4 py-2 rounded-lg disabled:opacity-50 transition-all duration-200 hover:opacity-90`}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Søker...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <Search className="w-5 h-5 mr-2" />
                    Søk
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Price Statistics */}
        {priceStats && (
          <div
            className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6 mb-6`}
          >
            <h3
              className={`text-xl font-semibold ${themeClasses.text.primary} mb-4`}
            >
              📊 Prisstatistikk
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div
                  className={`text-2xl font-bold ${themeClasses.text.primary}`}
                >
                  {formatPrice(priceStats.minPrice)}
                </div>
                <div className={`text-sm ${themeClasses.text.secondary}`}>
                  Laveste pris
                </div>
              </div>
              <div className="text-center">
                <div
                  className={`text-2xl font-bold ${themeClasses.text.primary}`}
                >
                  {formatPrice(priceStats.averagePrice)}
                </div>
                <div className={`text-sm ${themeClasses.text.secondary}`}>
                  Gjennomsnitt
                </div>
              </div>
              <div className="text-center">
                <div
                  className={`text-2xl font-bold ${themeClasses.text.primary}`}
                >
                  {formatPrice(priceStats.maxPrice)}
                </div>
                <div className={`text-sm ${themeClasses.text.secondary}`}>
                  Høyeste pris
                </div>
              </div>
              <div className="text-center">
                <div
                  className={`text-2xl font-bold ${themeClasses.text.primary}`}
                >
                  {priceStats.count}
                </div>
                <div className={`text-sm ${themeClasses.text.secondary}`}>
                  Klinikker
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Results */}
        {clinics.length > 0 && (
          <div
            className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
          >
            <div className="flex justify-between items-center mb-6">
              <h3
                className={`text-xl font-semibold ${themeClasses.text.primary}`}
              >
                Resultater ({clinics.length} klinikker)
              </h3>

              {/* Sorting Options */}
              <div className="flex space-x-2">
                <button
                  onClick={() => changeSorting("price")}
                  className={`px-3 py-1 rounded text-sm ${
                    searchParams.sortBy === "price"
                      ? `${themeClasses.bg.button} text-white`
                      : `${themeClasses.bg.secondary} ${themeClasses.text.primary}`
                  }`}
                >
                  <DollarSign className="w-4 h-4 inline mr-1" />
                  Pris
                  {searchParams.sortBy === "price" && (
                    <ArrowUpDown className="w-3 h-3 inline ml-1" />
                  )}
                </button>

                <button
                  onClick={() => changeSorting("distance")}
                  className={`px-3 py-1 rounded text-sm ${
                    searchParams.sortBy === "distance"
                      ? `${themeClasses.bg.button} text-white`
                      : `${themeClasses.bg.secondary} ${themeClasses.text.primary}`
                  }`}
                >
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Avstand
                  {searchParams.sortBy === "distance" && (
                    <ArrowUpDown className="w-3 h-3 inline ml-1" />
                  )}
                </button>

                <button
                  onClick={() => changeSorting("rating")}
                  className={`px-3 py-1 rounded text-sm ${
                    searchParams.sortBy === "rating"
                      ? `${themeClasses.bg.button} text-white`
                      : `${themeClasses.bg.secondary} ${themeClasses.text.primary}`
                  }`}
                >
                  <Star className="w-4 h-4 inline mr-1" />
                  Vurdering
                  {searchParams.sortBy === "rating" && (
                    <ArrowUpDown className="w-3 h-3 inline ml-1" />
                  )}
                </button>
              </div>
            </div>

            {/* Clinic Cards */}
            <div className="space-y-4">
              {clinics.map((clinic) => (
                <div
                  key={clinic.id}
                  className={`${themeClasses.bg.secondary} rounded-lg border ${themeClasses.border.primary} p-6 hover:shadow-lg transition-shadow duration-200`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4
                          className={`text-lg font-semibold ${themeClasses.text.primary}`}
                        >
                          {clinic.clinicName}
                        </h4>

                        {clinic.verified && (
                          <Award
                            className="w-5 h-5 text-green-500"
                            title="Verifisert klinikk"
                          />
                        )}

                        {/* Rating */}
                        {clinic.rating > 0 && (
                          <div className="flex items-center space-x-1">
                            <Star className="w-4 h-4 text-yellow-500 fill-current" />
                            <span
                              className={`text-sm ${themeClasses.text.secondary}`}
                            >
                              {clinic.rating} ({clinic.reviewCount})
                            </span>
                          </div>
                        )}
                      </div>

                      <div
                        className={`flex items-center space-x-4 text-sm ${themeClasses.text.secondary} mb-3`}
                      >
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          {clinic.address}, {clinic.city}
                        </div>

                        {clinic.distance && (
                          <div className="flex items-center">
                            <Navigation className="w-4 h-4 mr-1" />
                            {getDistanceText(clinic.distance)}
                          </div>
                        )}

                        {clinic.phoneNumber && (
                          <div className="flex items-center">
                            <Phone className="w-4 h-4 mr-1" />
                            {clinic.phoneNumber}
                          </div>
                        )}
                      </div>

                      {/* Specializations */}
                      {clinic.specializations &&
                        clinic.specializations.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {clinic.specializations
                              .slice(0, 3)
                              .map((spec, index) => (
                                <span
                                  key={index}
                                  className={`px-2 py-1 rounded text-xs ${themeClasses.bg.input} ${themeClasses.text.secondary}`}
                                >
                                  {spec}
                                </span>
                              ))}
                          </div>
                        )}
                    </div>

                    {/* Price */}
                    <div className="text-right">
                      {clinic.treatmentPrice ? (
                        <div>
                          <div
                            className={`text-2xl font-bold ${themeClasses.text.primary}`}
                          >
                            {formatPrice(clinic.treatmentPrice.price)}
                          </div>
                          {clinic.treatmentPrice.priceRange && (
                            <div
                              className={`text-sm ${themeClasses.text.secondary}`}
                            >
                              {clinic.treatmentPrice.priceRange}
                            </div>
                          )}
                          {clinic.treatmentPrice.notes && (
                            <div
                              className={`text-xs ${themeClasses.text.secondary} mt-1`}
                            >
                              {clinic.treatmentPrice.notes}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div
                          className={`text-sm ${themeClasses.text.secondary}`}
                        >
                          Pris ikke tilgjengelig
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
                    <div className="flex space-x-2">
                      <button
                        className={`flex items-center px-3 py-1 rounded text-sm ${themeClasses.bg.input} ${themeClasses.text.primary} hover:opacity-80`}
                      >
                        <Phone className="w-4 h-4 mr-1" />
                        Ring
                      </button>

                      <button
                        className={`flex items-center px-3 py-1 rounded text-sm ${themeClasses.bg.input} ${themeClasses.text.primary} hover:opacity-80`}
                      >
                        <MessageCircle className="w-4 h-4 mr-1" />
                        Vurdering
                      </button>

                      <button
                        className={`flex items-center px-3 py-1 rounded text-sm ${themeClasses.bg.input} ${themeClasses.text.primary} hover:opacity-80`}
                      >
                        <Building2 className="w-4 h-4 mr-1" />
                        Detaljer
                      </button>
                    </div>

                    <button
                      className={`flex items-center px-3 py-1 rounded text-sm ${themeClasses.bg.input} ${themeClasses.text.primary} hover:opacity-80`}
                    >
                      <Heart className="w-4 h-4 mr-1" />
                      Lagre
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No Results */}
        {!loading && clinics.length === 0 && searchParams.treatment && (
          <div className={`text-center py-12 ${themeClasses.text.secondary}`}>
            <Search className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-semibold mb-2">
              Ingen resultater funnet
            </h3>
            <p>Prøv å justere søkekriteriene eller velg en annen behandling.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VeterinaryPriceComparisonPage;
